import {
  reanchorAfterReboot, type ClockReading, type FramerateName, type TimecodeSpec,
} from "@infoflyt/shared/timecode";
import { supabase } from "./supabase";
import * as db from "./db";
import { erEkteProduksjon } from "./session";
import type { StoredAnchor } from "./types";

/**
 * Produksjonens tidskode ned til telefonen.
 *
 * ========  ER MAN PAA SAMME PRODUKSJON, HAR MAN SAMME TIDSKODE  ========
 *
 * Fram til 25.10.2026 laget hver enhet sitt eget anker: appen ett per
 * oppstart, nettflata ett til om den ikke fant noe. Tre ankere paa samme
 * opptaksdag var normalen, og de sa tre ulike ting — tidskoden i appen
 * stemte ikke med nettflata, og toppstripa stemte ikke med loggen.
 *
 * Naa gjelder regelen fra settet: JAMMER ÉN, HAR ALLE SATT DEN.
 *
 * ========  VERDIEN ADOPTERES, IKKE RADEN  ========
 *
 * Det enkleste hadde vaert aa bruke den andres rad ordrett. Men tidskoden
 * regnes fra ankerets MONOTONE klokke, og den finnes bare paa enheten som
 * satte det: alle andre maatte regnet fra veggklokka, og da ville hver
 * eneste post fra alle andre enn jammeren staatt som `usikker`. Flagget
 * betyr «den monotone tellingen brast», og det skal det fortsette aa bety.
 *
 * Derfor adopteres VERDIEN og uttrykkes paa vaar egen klokke — noeyaktig det
 * `reanchorAfterReboot` gjoer etter en omstart. Det koster den forskjellen
 * de to enhetenes veggklokker har akkurat da, og det er uansett den fysiske
 * grensen for hvor likt to enheter kan gaa.
 *
 * ========  BARE JAM ADOPTERES  ========
 *
 * `kind = 'jam'` betyr at noen har satt tidskoden mot en ekte referanse.
 * Uten det skillet ville enhetene adoptert hverandres avledninger i ring og
 * lagt paa litt klokkeavvik for hver runde.
 *
 * Fram til 27.10.2026 sto det `derived_from is null`, og det var for vidt:
 * ankeret appen lager ved oppstart — «ny logg starter med tidskoden satt
 * til naavaerende klokkeslett» — har heller ikke noe opphav, og ble derfor
 * lest som en jam av alle andre. Se migrasjon 20261027091000.
 *
 * PRIVAT GAAR INGEN VEI. En privat logg hoerer ingen produksjon til, og da
 * jammes den paa telefonen som foer.
 */

/** Det som ikke gikk sier fra. Samme moenster som loggsynk. */
function klag(feil: unknown): void {
  // eslint-disable-next-line no-console
  console.error("[infoflyt] tidskodesynk", feil);
}

/**
 * Henter produksjonens jam for dagen og adopterer den om den er ny.
 *
 * Returnerer det nye ankeret, eller null naar ingenting skal endres —
 * uten nett, paa privat, eller naar vi alt foelger den samme jammen.
 *
 * KALLES VED DAGENS AAPNING OG PAA TAKTEN. En jam midt i skiftet er en
 * normal handling, ikke en feilretting, og den skal naa fram til de andre
 * mens de logger. Se CLAUDE.md.
 */
export async function hentJam(
  produksjonId: string,
  opptaksdag: string,
  vaart: StoredAnchor | null,
  avlesning: ClockReading,
  spec: TimecodeSpec,
): Promise<StoredAnchor | null> {
  if (supabase === null || !erEkteProduksjon(produksjonId)) return null;
  if (!navigator.onLine) return null;

  const { data, error } = await supabase
    .from("tc_anchor")
    .select("id, tc_frames, framerate, drop_frame, monotonic_ms, boot_id, wall_utc, source")
    .eq("production_id", produksjonId)
    .eq("shoot_day", opptaksdag)
    .eq("kind", "jam")
    .order("wall_utc", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error !== null) { klag(error); return null; }
  if (data === null) return null;

  const r = data as unknown as Record<string, unknown>;
  const jamId = String(r["id"]);

  /*
   * ER DET VAART EGET, ELLER DET VI ALT FOELGER, SKJER INGENTING.
   *
   * Uten begge vaktene ville hver takt laget et nytt anker: det foerste
   * fordi vi adopterte vaar egen jam i ring, det andre fordi vi adopterte
   * den samme jammen om igjen. Da ville tidskoden drevet ett klokkeavvik
   * hvert fjerde sekund.
   */
  if (vaart !== null && (vaart.id === jamId || vaart.derivedFrom === jamId)) return null;

  const jam: StoredAnchor = {
    id: jamId,
    productionId: produksjonId,
    shootDay: opptaksdag,
    /* Jammens egen tid, slik den staar paa serveren. Det er DEN de 24
       timene telles fra — ikke naar vi adopterte den. */
    jammetUtcMs: Date.parse(String(r["wall_utc"])),
    tcFrames: Number(r["tc_frames"]),
    framerate: String(r["framerate"]) as FramerateName,
    dropFrame: Boolean(r["drop_frame"]),
    monotonicMs: Number(r["monotonic_ms"]),
    bootId: String(r["boot_id"]),
    wallUtcMs: Date.parse(String(r["wall_utc"])),
    source: "manual",
    kind: "jam",
    derivedFrom: null,
  };

  /*
   * Samme tidskode, vaar klokke. `reanchorAfterReboot` regner veggklokka som
   * DIFFERANSE og ikke som absolutt verdi — det er nettopp den regningen som
   * skal til for aa uttrykke en annens anker paa vaar egen monotone telling.
   *
   * ========  ET KLOKKEANKER BYTTER VERDI PAA PLASS  ========
   *
   * Sto vi paa vaar egen klokke, beholder vi ID-EN. Postene peker paa
   * ankeret, saa alt som er logget hittil i dag flytter seg med — nettopp
   * det `adjustAnchorTo` gjoer naar man justerer selv.
   *
   * Det er forutsetningen for at regi kan begynne aa logge foer tidskoden
   * er satt for dagen. Regi jammer ikke lenger (avgjort 27.10.2026), saa
   * dagen starter paa telefonens klokke og rettes naar teknikeren jammer.
   * Lagde vi et nytt anker her i stedet, ville postene fra foer jammen
   * blitt staaende paa en klokke ingen hadde avtalt, mens de etter fulgte
   * boksen — to tidskoder i samme skift.
   *
   * Fulgte vi alt en annen jam, lages et nytt anker som foer: da er
   * postene fra foer riktige mot DEN jammen, og historikken skal ikke
   * skrives om.
   */
  const paaEgenKlokke = vaart !== null && vaart.kind === "klokke";

  const vaartNye: StoredAnchor = {
    ...reanchorAfterReboot(jam, avlesning, spec),
    id: paaEgenKlokke && vaart !== null ? vaart.id : db.newId(),
    productionId: produksjonId,
    shootDay: opptaksdag,
    kind: "avledet",
    derivedFrom: jamId,
    /* Adopsjonen arver tidspunktet. Uten dette ville en jam fra i gaar
       sett fersk ut i det den ble adoptert. */
    jammetUtcMs: jam.jammetUtcMs,
  };

  await db.openDb(produksjonId);
  await db.saveAnchor(vaartNye);
  await db.audit(
    produksjonId, "tc_anchor", vaartNye.id, "anchor_adopted",
    vaart === null ? "" : String(vaart.tcFrames), String(vaartNye.tcFrames),
  );
  return vaartNye;
}
